import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, ExternalLink, Phone, Facebook, Instagram, Globe } from "lucide-react";
import CategoryHero from "./CategoryHero";
import { useManagedCollection, slugify } from "../admin/useManagedCollection";
import { AdminItem, AdminAddBar } from "../admin/AdminControls";
import type { FieldDef } from "../admin/AdminFieldModal";
import NewsTicker from "./NewsTicker";
import SocialLinks, { SOCIAL_FIELDS } from "./SocialLinks";

const HOME_SERVICE_FIELDS: FieldDef[] = [
  { key: "name", label: "Name", required: true },
  { key: "description", label: "Services offered", placeholder: "Event assistance, bartending, cleaning" },
  { key: "website", label: "Website URL", type: "url", placeholder: "https://…" },
  { key: "address", label: "Address", type: "textarea" },
  { key: "phone", label: "Contact phone", placeholder: "(469) 343-4228" },
  { key: "image", label: "Image URL", type: "image", placeholder: "https://…" },
  { key: "socialMediaUrl", label: "Social media URL", type: "url", placeholder: "Facebook, Instagram, or website link — shows as a big button" },
  ...SOCIAL_FIELDS,
];

export interface HomeServiceEntry {
  name: string;
  description: string;
  website?: string;
  address?: string;
  phone?: string;
  image: string;
  hideViewLocation?: boolean;
  instagram?: string;
  socialMediaUrl?: string;
}

function socialButtonMeta(url: string) {
  const lower = url.toLowerCase();
  if (lower.includes("facebook.com") || lower.includes("fb.com")) {
    return { label: "Facebook", Icon: Facebook, className: "bg-[#1877F2] hover:bg-[#0f5fcc]" };
  }
  if (lower.includes("instagram.com")) {
    return {
      label: "Instagram",
      Icon: Instagram,
      className: "bg-gradient-to-r from-[#f58529] via-[#dd2a7b] to-[#8134af] hover:opacity-90",
    };
  }
  return { label: "Visit Page", Icon: Globe, className: "bg-[#f59e0b] hover:bg-[#d98a08]" };
}

export const HOME_SERVICES_ENTRIES: HomeServiceEntry[] = [
  {
    name: "Duct Time - Air Duct Cleaning",
    description: "Air duct cleaning",
    website: "https://callducttime.com/",
    phone: "972-877-2500",
    address: "",
    image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-14/f57a22668562.jpeg",
    hideViewLocation: true,
  },
  {
    name: "BLINDS ON TIME TX",
    description: "Blinds, Shades, Curtains & Sheer",
    address: "",
    phone: "945-289-7199",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784878353306-45isi.jpg",
    hideViewLocation: true,
  },
  {
    name: "LedKing Fortworth",
    description: "All type of residential and commercial lightings Chandeliers, Downlights, pendants, parking lot lights, panel sheets for under Counter lightings modern lights which make your home brighter and looks Modern & stylish",
    address: "",
    phone: "682-200-3004",
    image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-09-23/c7405bca8760.jpeg",
    hideViewLocation: true,
  },
  {
    name: "Devi Woodworks",
    description: "Custom closets, cabinetry & handcrafted woodwork",
    address: "",
    phone: "817-697-1454",
    image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-10-12/833f7e23c986.jpeg",
    hideViewLocation: true,
    socialMediaUrl: "https://www.instagram.com/devi_woodworks?exln=cXN5emduYmgycDg0&utm_source=qr",
  },
  {
    name: "Power Hopper - Shop For Electricity",
    description: "",
    website: "https://powerhopper.energy",
    address: "",
    image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-10-23/e60577610061.png",
    hideViewLocation: true,
  },
];

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=600&h=400&fit=crop";

function mapsUrl(e: HomeServiceEntry) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${e.name}, ${e.address}`)}`;
}

export function HomeServiceCard({ entry, index }: { entry: HomeServiceEntry; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className="bg-white rounded-md border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <div className="w-full h-[180px] relative overflow-hidden bg-gray-100 flex items-center justify-center">
        <img
          src={entry.image || FALLBACK_IMAGE}
          alt={entry.name}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
          }}
        />
      </div>
      <div className="p-1.5 pb-0.5 flex-1 flex flex-col justify-between">
        <div>
          <h3 className="text-[10px] font-bold text-[#082f6d] leading-tight truncate">
            <Link to={`/home-services/${slugify(entry.name)}`} className="hover:underline">
              {entry.name}
            </Link>
          </h3>
          {entry.address && (
            <div className="flex items-start gap-0.5 mt-0.5">
              <MapPin className="w-2.5 h-2.5 text-orange-500 shrink-0 mt-0.5" />
              <span className="text-[9px] text-gray-500 font-medium leading-snug truncate">
                {entry.address}
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="px-1.5 pb-1.5 pt-0.5 flex flex-col gap-1">
        {/* website has its own dedicated "Visit Website" button below, so exclude it
            from the icon row to avoid a duplicate/redundant website icon. "Electrician
            On Call" never had a Facebook link supplied — an auto-filled field was
            surfacing the icon anyway, so it's explicitly cleared for that card. */}
        <SocialLinks
          item={{
            ...entry,
            website: undefined,
            ...(entry.name?.toLowerCase().includes("electrician on call")
              ? { facebook: undefined, facebookUrl: undefined, facebook_url: undefined }
              : {}),
          }}
          px={20}
        />
        <div className="flex flex-wrap gap-1.5">
          {entry.website && (
            <form action={entry.website} method="get" target="_top" className="flex-1 min-w-[70px]">
              <button
                type="submit"
                className="w-full flex items-center justify-center gap-1 bg-[#082f6d] hover:bg-[#062454] text-white text-[9px] font-semibold py-1.5 px-1.5 rounded-md transition-colors duration-200 cursor-pointer"
              >
                <ExternalLink className="w-2.5 h-2.5" /> Website
              </button>
            </form>
          )}
          {entry.phone && (
            <a
              href={`tel:${entry.phone.replace(/[^\d+]/g, "")}`}
              className="flex-1 min-w-[70px] flex items-center justify-center gap-1 bg-[#51ba4f] hover:bg-[#459f43] text-white text-[9px] font-semibold py-1.5 px-1.5 rounded-md transition-colors duration-200 cursor-pointer"
            >
              <Phone className="w-2.5 h-2.5" /> Call
            </a>
          )}
          {entry.socialMediaUrl && (() => {
            const { label, Icon, className } = socialButtonMeta(entry.socialMediaUrl!);
            return (
              <a
                href={entry.socialMediaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex-1 min-w-[70px] flex items-center justify-center gap-1 ${className} text-white text-[9px] font-semibold py-1.5 px-1.5 rounded-md transition-colors duration-200 cursor-pointer`}
              >
                <Icon className="w-2.5 h-2.5" /> {label}
              </a>
            );
          })()}
          {entry.address && !entry.hideViewLocation && (
            <a
              href={mapsUrl(entry)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 min-w-[70px] flex items-center justify-center gap-1 bg-blue-600 hover:bg-blue-700 text-white text-[9px] font-semibold py-1.5 px-1.5 rounded-md transition-colors duration-200"
            >
              <ExternalLink className="w-2.5 h-2.5" /> View Location
            </a>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default function HomeServices() {
  const [query, setQuery] = useState("");
  const mc = useManagedCollection<any>("home_services", HOME_SERVICES_ENTRIES, (e, i) => slugify(e.name || String(i)));

  const filtered = mc.items.filter(
    (e) =>
      query.trim() === "" ||
      e.name.toLowerCase().includes(query.trim().toLowerCase()) ||
      (e.description || "").toLowerCase().includes(query.trim().toLowerCase())
  );

  return (
    <div className="min-h-screen bg-white">
      <CategoryHero
        title="Home Services"
        placeholder="Search home service professionals…"
        backgroundImage="https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=1440&h=400&fit=crop"
        query={query}
        setQuery={setQuery}
      />
      <NewsTicker />

      <section
        className="bg-gray-50/40 py-8"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 flex flex-col lg:flex-row gap-6">
          <div className="flex-1 min-w-0">
            <h2 className="text-[16px] md:text-[18px] font-bold text-gray-900 mb-1">
              Home Service Professionals
            </h2>
            <p className="text-[13px] text-gray-500 mb-5">
              Find South Asian home service professionals in DFW
            </p>
            <AdminAddBar mc={mc} fields={HOME_SERVICE_FIELDS} label="Add Listing" />
            {filtered.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1.5">
                {filtered.map((e, i) => (
                  <AdminItem key={e._key} mc={mc} item={e} fields={HOME_SERVICE_FIELDS} title="Listing">
                    <HomeServiceCard entry={e as any} index={i} />
                  </AdminItem>
                ))}
              </div>
            ) : (
              <div className="bg-white border border-dashed border-gray-200 rounded-2xl p-8 text-center text-[13px] text-gray-500">
                No home service listings yet.
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
